import React, { useState, useEffect, useCallback } from 'react';
import negocioParcelaService from '../services/negocioParcela.service';

const COR = {
  azul:     '#003399', azulBg:  '#eff6ff',
  borda:    '#e8eaed', texto:   '#1a1a2e', muted: '#888',
  branco:   '#fff',    cinzaBg: '#fafafa',
  vermelho: '#e53935', vermBg:  '#fff0f0',
  verde:    '#1D9E75', verdeBg: '#f0fff4',
  amarelo:  '#d97706', amlBg:   '#fffbeb',
};

const inputStyle = {
  width: '100%', height: '32px', padding: '0 8px',
  border: `1px solid ${COR.borda}`, borderRadius: '4px',
  fontSize: '12px', boxSizing: 'border-box',
  backgroundColor: COR.branco, color: COR.texto, outline: 'none',
};
const inputDisabledStyle = {
  ...inputStyle, backgroundColor: COR.cinzaBg, color: COR.muted,
};

function formatBR(n, casas = 2) {
  if (n === null || n === undefined || n === '') return '';
  const num = parseFloat(n);
  if (isNaN(num)) return '';
  return num.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas });
}

function centavosParaBR(input) {
  if (input === '' || input === null || input === undefined) return '';
  const digitos = String(input).replace(/\D/g, '');
  if (!digitos) return '';
  const num = parseInt(digitos, 10) / 100;
  return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function parseBR(v) {
  if (v === '' || v === null || v === undefined) return 0;
  const norm = String(v).replace(/\./g, '').replace(',', '.');
  const num = parseFloat(norm);
  return isNaN(num) ? 0 : num;
}

function PrevisaoFinanceiraModal({ aberto, negocio, usuarioId, onFechar, onSucesso }) {
  const [parcelas, setParcelas]     = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando]     = useState(false);
  const [erro, setErro]             = useState('');
  const [sucesso, setSucesso]       = useState('');

  // Soma das parcelas deve bater com o VALOR ATUAL (não bruto).
  // Valor Atual = Bruto − Desconto + Acréscimo. É o que o cliente paga.
  const valorAtual = parseFloat(negocio?.valor_atual) || 0;

  const carregar = useCallback(async () => {
    if (!negocio?.id) return;
    try {
      setCarregando(true);
      setErro('');
      const dados = await negocioParcelaService.listar(negocio.id);
      setParcelas(dados.map(p => ({
        id: p.id,
        numero: p.numero,
        data_vencimento: p.data_vencimento ? String(p.data_vencimento).slice(0, 10) : '',
        valor: formatBR(p.valor),
        observacao: p.observacao || '',
        status: p.status || 'P',
      })));
    } catch (err) {
      console.error('Erro ao carregar parcelas:', err);
      setErro('Erro ao carregar parcelas.');
    } finally {
      setCarregando(false);
    }
  }, [negocio?.id]);

  useEffect(() => {
    if (aberto) carregar();
  }, [aberto, carregar]);

  // Negócio precisa estar Fechado pra editar
  const podeEditar = negocio?.etapa_comercial === 'F';

  const atualizarParcela = (idx, campo, valor) => {
    setParcelas(ps => ps.map((p, i) => i === idx ? { ...p, [campo]: valor } : p));
  };

  const somaParcelas = parcelas.reduce((s, p) => s + parseBR(p.valor), 0);
  const diferenca    = somaParcelas - valorAtual;
  const somaOk       = Math.abs(diferenca) < 0.01;

  const salvar = async () => {
    setErro(''); setSucesso('');
    if (!podeEditar) return;
    if (!somaOk) {
      return setErro(`A soma das parcelas (R$ ${formatBR(somaParcelas)}) deve ser igual ao Valor Atual (R$ ${formatBR(valorAtual)}).`);
    }
    for (const p of parcelas) {
      if (!p.data_vencimento) return setErro('Toda parcela precisa de data de vencimento.');
      if (parseBR(p.valor) <= 0) return setErro('Toda parcela precisa de valor maior que zero.');
    }

    try {
      setSalvando(true);
      await negocioParcelaService.substituir(negocio.id, {
        parcelas: parcelas.map(p => ({
          data_vencimento: p.data_vencimento,
          valor: parseBR(p.valor),
          observacao: p.observacao || null,
        })),
        usuario_id: usuarioId,
        valor_esperado: valorAtual,
      });
      setSucesso('Parcelas atualizadas com sucesso!');
      setTimeout(() => setSucesso(''), 2500);
      if (onSucesso) onSucesso();
    } catch (err) {
      setErro(err.response?.data?.mensagem || 'Erro ao salvar parcelas.');
    } finally {
      setSalvando(false);
    }
  };

  if (!aberto) return null;

  const Z_OVERLAY = 10000;
  const Z_MODAL = 10001;

  return (
    <>
      <div onClick={onFechar} style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.4)', zIndex: Z_OVERLAY,
      }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '92%', maxWidth: '750px', maxHeight: '85vh',
        backgroundColor: COR.branco, borderRadius: '8px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
        display: 'flex', flexDirection: 'column',
        zIndex: Z_MODAL, overflow: 'hidden',
        fontFamily: "'Manrope', sans-serif",
      }}>
        <div style={{
          padding: '14px 20px', borderBottom: `1px solid ${COR.borda}`,
          display: 'flex', alignItems: 'center', gap: '12px',
          backgroundColor: COR.azul, color: COR.branco,
        }}>
          <span style={{ fontSize: '18px' }}>💰</span>
          <span style={{ fontSize: '15px', fontWeight: '600', flex: 1 }}>
            Previsão Financeira · {negocio?.id}
          </span>
          <button onClick={onFechar} style={{
            border: 'none', background: 'transparent', color: COR.branco,
            fontSize: '20px', cursor: 'pointer',
          }}>×</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px' }}>
          {!podeEditar && (
            <div style={{
              backgroundColor: COR.amlBg, border: `1px solid ${COR.amarelo}55`,
              borderRadius: '5px', color: '#92400e', fontSize: '12px',
              padding: '8px 10px', marginBottom: '10px', fontWeight: '600',
            }}>
              ⚠️ As parcelas só podem ser editadas quando o negócio está na etapa "Fechado".
            </div>
          )}
          {erro && (
            <div style={{
              backgroundColor: COR.vermBg, border: '1px solid #ffcccc',
              borderRadius: '5px', color: COR.vermelho, fontSize: '12px',
              padding: '8px 10px', marginBottom: '10px',
            }}>{erro}</div>
          )}
          {sucesso && (
            <div style={{
              backgroundColor: COR.verdeBg, border: '1px solid #a8e6c1',
              borderRadius: '5px', color: '#1d6a3a', fontSize: '12px',
              padding: '8px 10px', marginBottom: '10px',
            }}>{sucesso}</div>
          )}

          {/* Resumo */}
          <div style={{
            backgroundColor: COR.azulBg, border: `1px solid ${COR.azul}33`,
            borderRadius: '6px', padding: '10px 14px', marginBottom: '12px',
            display: 'flex', gap: '20px',
          }}>
            <div>
              <div style={{ fontSize: '10px', fontWeight: '700', color: COR.azul,
                            textTransform: 'uppercase', letterSpacing: '0.5px' }}>Valor Atual</div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: COR.azul }}>
                R$ {formatBR(valorAtual)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '10px', fontWeight: '700', color: COR.azul,
                            textTransform: 'uppercase', letterSpacing: '0.5px' }}>Parcelas</div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: COR.azul }}>
                {parcelas.length}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '10px', fontWeight: '700', color: COR.azul,
                            textTransform: 'uppercase', letterSpacing: '0.5px' }}>Soma</div>
              <div style={{ fontSize: '16px', fontWeight: '700',
                            color: somaOk ? COR.verde : COR.vermelho }}>
                R$ {formatBR(somaParcelas)} {somaOk ? '✓' : '⚠️'}
              </div>
            </div>
          </div>

          {carregando ? (
            <div style={{ textAlign: 'center', padding: '30px', color: COR.muted, fontSize: '13px' }}>
              Carregando...
            </div>
          ) : parcelas.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px', color: COR.muted, fontSize: '13px' }}>
              Nenhuma parcela cadastrada. As parcelas são criadas no fechamento do negócio.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ backgroundColor: COR.cinzaBg }}>
                  <th style={ThStyle}>#</th>
                  <th style={ThStyle}>Data Vencimento</th>
                  <th style={ThStyle}>Valor (R$)</th>
                  <th style={ThStyle}>Status</th>
                  <th style={ThStyle}>Observação</th>
                </tr>
              </thead>
              <tbody>
                {parcelas.map((p, idx) => (
                  <tr key={idx} style={{ borderBottom: `1px solid ${COR.borda}` }}>
                    <td style={{ ...TdStyle, fontWeight: '600' }}>{idx + 1}</td>
                    <td style={TdStyle}>
                      <input type="date" value={p.data_vencimento}
                        onChange={e => atualizarParcela(idx, 'data_vencimento', e.target.value)}
                        disabled={!podeEditar}
                        style={podeEditar ? inputStyle : inputDisabledStyle} />
                    </td>
                    <td style={TdStyle}>
                      <input value={p.valor}
                        onChange={e => atualizarParcela(idx, 'valor', centavosParaBR(e.target.value))}
                        disabled={!podeEditar}
                        style={podeEditar
                          ? { ...inputStyle, textAlign: 'right' }
                          : { ...inputDisabledStyle, textAlign: 'right' }} />
                    </td>
                    <td style={{ ...TdStyle, textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px',
                        borderRadius: '20px', fontSize: '10px', fontWeight: '600',
                        backgroundColor: p.status === 'R' ? COR.verdeBg : COR.amlBg,
                        color: p.status === 'R' ? COR.verde : COR.amarelo,
                      }}>{p.status === 'R' ? 'Recebido' : 'Previsto'}</span>
                    </td>
                    <td style={TdStyle}>
                      <input value={p.observacao}
                        onChange={e => atualizarParcela(idx, 'observacao', e.target.value)}
                        disabled={!podeEditar}
                        style={podeEditar ? inputStyle : inputDisabledStyle}
                        placeholder="—" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        {podeEditar && parcelas.length > 0 && (
          <div style={{
            padding: '12px 20px', borderTop: `1px solid ${COR.borda}`,
            display: 'flex', gap: '10px', justifyContent: 'flex-end',
            backgroundColor: COR.cinzaBg,
          }}>
            <button onClick={onFechar} disabled={salvando}
              style={{
                padding: '8px 18px', fontSize: '13px', fontWeight: '600',
                border: `1px solid ${COR.borda}`, borderRadius: '5px',
                backgroundColor: COR.branco, color: COR.texto,
                cursor: 'pointer',
              }}>Fechar</button>
            <button onClick={salvar} disabled={salvando || !somaOk}
              style={{
                padding: '8px 22px', fontSize: '13px', fontWeight: '600',
                border: 'none', borderRadius: '5px',
                backgroundColor: somaOk ? COR.verde : COR.muted,
                color: COR.branco,
                cursor: somaOk && !salvando ? 'pointer' : 'not-allowed',
              }}>{salvando ? 'Salvando...' : '💾 Salvar'}</button>
          </div>
        )}
      </div>
    </>
  );
}

const ThStyle = {
  padding: '8px 6px', textAlign: 'left',
  fontSize: '10px', fontWeight: '700', color: COR.muted,
  textTransform: 'uppercase', letterSpacing: '0.4px',
};
const TdStyle = { padding: '6px 4px', verticalAlign: 'middle' };

export default PrevisaoFinanceiraModal;
